import { ChangeDetectionStrategy, Component, ElementRef, NgZone, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Toast } from 'ngx-toastr';

import type { RcToastAction, RcToastPayload, RcToastVariant } from './rc-toast.model';

@Component({
  selector: 'app-rc-toast',
  template: `
    @let payload = toastPayload();
    @let _options = options();

    <section
      class="rc-toast"
      [class.rc-toast--neutral]="isNeutralToast()"
      [class.rc-toast--expanded]="hasExpandedContent()"
      [attr.role]="statusRole()"
      [attr.aria-live]="ariaLive()"
      aria-atomic="true">
      <div class="rc-toast__icon" aria-hidden="true">
        @if (iconClass()) {
          <i [class]="iconClass()"></i>
        } @else {
          <svg
            class="rc-toast__icon-svg"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              [attr.d]="iconPath()" />
          </svg>
        }
      </div>

      <div class="rc-toast__body">
        @if (payload.eyebrow) {
          <div class="rc-toast__eyebrow">{{ payload.eyebrow }}</div>
        }

        @if (title()) {
          <div [class]="titleClasses()">
            {{ title() }}

            @if (duplicatesCount) {
              <span class="rc-toast__duplicate">x{{ duplicatesCount + 1 }}</span>
            }
          </div>
        }

        @if (message()) {
          @if (_options.enableHtml) {
            <div [class]="messageClasses()" [innerHTML]="message()"></div>
          } @else {
            <div [class]="messageClasses()">{{ message() }}</div>
          }
        }

        @if (payload.description) {
          <div class="rc-toast__description">{{ payload.description }}</div>
        }

        @if (payload.meta?.length) {
          <div class="rc-toast__meta">
            @for (meta of payload.meta; track meta) {
              <span>{{ meta }}</span>
            }
          </div>
        }

        @if (payload.actions?.length) {
          <div
            class="rc-toast__actions"
            [class.rc-toast__actions--split]="payload.actionsLayout === 'split'">
            @for (action of payload.actions; track action.id ?? action.label) {
              @if (action.href) {
                <a
                  [class]="actionClasses(action)"
                  [href]="action.href"
                  [attr.target]="action.target ?? null"
                  [attr.rel]="action.target === '_blank' ? 'noopener noreferrer' : null"
                  [attr.aria-label]="action.ariaLabel ?? action.label"
                  (click)="runLinkAction(action, $event)">
                  {{ action.label }}
                </a>
              } @else {
                <button
                  type="button"
                  [class]="actionClasses(action)"
                  [class.rc-toast__action--blocked]="isActionConfirmationBlocked(action)"
                  [class.rc-toast__action--ready]="isActionConfirmationReady(action)"
                  [attr.aria-disabled]="isActionConfirmationBlocked(action)"
                  [attr.aria-label]="actionAriaLabel(action)"
                  [attr.title]="actionTitle(action)"
                  (mouseenter)="startActionConfirmation(action)"
                  (mouseleave)="cancelActionConfirmation(action)"
                  (focus)="startActionConfirmation(action)"
                  (blur)="cancelActionConfirmation(action)"
                  (click)="runButtonAction(action, $event)">
                  {{ actionLabel(action) }}
                </button>
              }
            }
          </div>
        }
      </div>

      @if (_options.closeButton) {
        <button
          type="button"
          class="rc-toast__close toast-close-button"
          aria-label="Fermer la notification"
          (click)="closeToast($event)">
          <svg
            class="rc-toast__close-icon"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18 17.94 6M18 18 6.06 6" />
          </svg>
        </button>
      }

      @if (_options.progressBar) {
        <div class="rc-toast__progress" aria-hidden="true">
          <div class="toast-progress rc-toast__progress-bar" [style.width]="width() + '%'"></div>
        </div>
      }

    </section>

    <svg class="rc-toast__ring" aria-hidden="true" focusable="false">
      <path class="rc-toast__ring-path" [attr.d]="ringPath()" pathLength="1" />
    </svg>
  `,
  styleUrl: './rc-toast.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[style.display]': 'displayStyle()',
    '[style.--animation-easing]': 'params.easing',
    '[style.--animation-duration]': 'params.easeTime + "ms"',
    'animate.enter': 'toast-in',
  },
})
export class RcToastComponent extends Toast<RcToastPayload> {
  private readonly router = inject(Router);
  private readonly actionConfirmationIntervals = new Map<string, number>();
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);

  readonly ringPath = signal('');

  override stickAround(): void {
    super.stickAround();
    this.resetAndPauseRing();
  }

  override delayedHideToast(): void {
    super.delayedHideToast();
    this.restartRingForExtendedTimeout();
  }

  private resetAndPauseRing(): void {
    const path = this.hostRef.nativeElement.querySelector<SVGPathElement>('.rc-toast__ring-path');
    if (!path) return;

    path.style.animationName = 'none';
    void path.getBoundingClientRect();
    path.style.animationName = '';
    path.style.animationPlayState = 'paused';
  }

  private restartRingForExtendedTimeout(): void {
    const extendedTimeOut = this.options().extendedTimeOut;
    if (!extendedTimeOut) return;

    const host = this.hostRef.nativeElement;
    const path = host.querySelector<SVGPathElement>('.rc-toast__ring-path');
    if (!path) return;

    host.style.setProperty('--toast-timeout', `${extendedTimeOut}ms`);
    path.style.animationPlayState = '';
    path.style.animationName = 'none';
    void path.getBoundingClientRect(); // force reflow pour que le navigateur "oublie" l'animation
    path.style.animationName = '';
  }

  ngAfterViewInit(): void {
    const host = this.hostRef.nativeElement;

    // Capture BEFORE stickAround() can set options().timeOut to 0 on first hover
    host.style.setProperty('--toast-timeout', `${this.options().timeOut}ms`);

    const section = host.querySelector<HTMLElement>('.rc-toast');
    if (!section) return;

    const observer = new ResizeObserver(() => {
      const w = section.offsetWidth;
      const h = section.offsetHeight;
      if (w > 0 && h > 0) {
        observer.disconnect();
        this.ngZone.run(() => this.ringPath.set(this.buildRingPath(section)));
      }
    });
    observer.observe(section);
  }

  private buildRingPath(el: HTMLElement): string {
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const r = 13;   // border-radius (14px) − 1px d'inset
    const i = 1;    // inset pour que le stroke reste dans le card
    const right  = w - i;
    const bottom = h - i;
    const midY  = h / 2;

    return [
      `M${i},${midY}`,
      `L${i},${i + r}`,
      `A${r},${r},0,0,1,${i + r},${i}`,
      `L${right - r},${i}`,
      `A${r},${r},0,0,1,${right},${i + r}`,
      `L${right},${bottom - r}`,
      `A${r},${r},0,0,1,${right - r},${bottom}`,
      `L${i + r},${bottom}`,
      `A${r},${r},0,0,1,${i},${bottom - r}`,
      `L${i},${midY}`,
    ].join(' ');
  }

  readonly toastPayload = computed(() => this.normalizePayload(this.options().payload));
  readonly actionConfirmationRemaining = signal<Record<string, number>>({});
  readonly variant = computed(() => this.toastPayload().variant ?? this.variantFromToastType(this.toastPackage.toastType));
  readonly isNeutralToast = computed(() => this.variant() === 'info' || this.variant() === 'default');
  readonly iconClass = computed(() => this.toastPayload().iconClass ?? '');
  readonly iconPath = computed(() => this.defaultIconPath(this.variant()));
  readonly titleClasses = computed(() => this.mergeClasses('rc-toast__title', this.options().titleClass));
  readonly messageClasses = computed(() => this.mergeClasses('rc-toast__message', this.options().messageClass));
  readonly hasExpandedContent = computed(() => {
    const payload = this.toastPayload();
    return Boolean(payload.eyebrow || payload.description || payload.meta?.length || payload.actions?.length || this.title());
  });
  readonly hostClasses = computed(() => this.toastClasses());
  readonly statusRole = computed(() => this.variant() === 'error' || this.variant() === 'warning' ? 'alert' : 'status');
  readonly ariaLive = computed(() => this.variant() === 'error' || this.variant() === 'warning' ? 'assertive' : 'polite');

  override ngOnDestroy(): void {
    this.actionConfirmationIntervals.forEach((intervalId) => window.clearInterval(intervalId));
    this.actionConfirmationIntervals.clear();
    super.ngOnDestroy();
  }

  protected closeToast(event: MouseEvent): void {
    event.stopPropagation();
    this.toastPayload().onClose?.();
    this.remove();
  }

  protected runLinkAction(action: RcToastAction, event: MouseEvent): void {
    event.stopPropagation();
    action.callback?.();

    if (action.dismissOnClick !== false) {
      this.remove();
    }
  }

  protected runButtonAction(action: RcToastAction, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isActionConfirmationBlocked(action)) {
      this.startActionConfirmation(action);
      return;
    }

    action.callback?.();

    if (action.route?.length) {
      void this.router.navigate([...action.route], { queryParams: action.queryParams });
    }

    if (action.dismissOnClick !== false) {
      this.remove();
    }
  }

  protected actionClasses(action: RcToastAction): string {
    const variant = action.variant ?? 'primary';
    return `rc-toast__action rc-toast__action--${variant}`;
  }

  protected actionLabel(action: RcToastAction): string {
    const remaining = this.getActionConfirmationRemaining(action);
    return remaining && remaining > 0 ? `${action.label} (${remaining}s)` : action.label;
  }

  protected actionAriaLabel(action: RcToastAction): string {
    const label = action.ariaLabel ?? action.label;
    const remaining = this.getActionConfirmationRemaining(action) ?? this.getActionConfirmationSeconds(action);

    if (remaining > 0) {
      const secondsLabel = remaining > 1 ? 'secondes' : 'seconde';
      return `${label}. Confirmation disponible dans ${remaining} ${secondsLabel}.`;
    }

    return label;
  }

  protected actionTitle(action: RcToastAction): string | null {
    const seconds = this.getActionConfirmationSeconds(action);
    if (seconds <= 0) {
      return null;
    }

    if (this.isActionConfirmationReady(action)) {
      return 'Confirmation disponible.';
    }

    return `Survolez ou gardez le focus ${seconds} s pour confirmer.`;
  }

  protected startActionConfirmation(action: RcToastAction): void {
    const seconds = this.getActionConfirmationSeconds(action);
    if (seconds <= 0 || this.isActionConfirmationReady(action)) {
      return;
    }

    const key = this.actionKey(action);
    if (this.actionConfirmationIntervals.has(key)) {
      return;
    }

    this.actionConfirmationRemaining.update((state) => ({ ...state, [key]: seconds }));

    let remaining = seconds;
    const intervalId = window.setInterval(() => {
      remaining -= 1;
      this.actionConfirmationRemaining.update((state) => ({ ...state, [key]: Math.max(remaining, 0) }));

      if (remaining <= 0) {
        window.clearInterval(intervalId);
        this.actionConfirmationIntervals.delete(key);
      }
    }, 1000);

    this.actionConfirmationIntervals.set(key, intervalId);
  }

  protected cancelActionConfirmation(action: RcToastAction): void {
    const key = this.actionKey(action);
    const remaining = this.actionConfirmationRemaining()[key];
    const intervalId = this.actionConfirmationIntervals.get(key);

    if (intervalId !== undefined) {
      window.clearInterval(intervalId);
      this.actionConfirmationIntervals.delete(key);
    }

    if (remaining !== undefined && remaining > 0) {
      this.actionConfirmationRemaining.update((state) => {
        const next = { ...state };
        delete next[key];
        return next;
      });
    }
  }

  protected isActionConfirmationBlocked(action: RcToastAction): boolean {
    return this.getActionConfirmationSeconds(action) > 0 && this.getActionConfirmationRemaining(action) !== 0;
  }

  protected isActionConfirmationReady(action: RcToastAction): boolean {
    return this.getActionConfirmationSeconds(action) > 0 && this.getActionConfirmationRemaining(action) === 0;
  }

  private normalizePayload(payload: RcToastPayload | undefined): RcToastPayload {
    return payload ?? {};
  }

  private getActionConfirmationRemaining(action: RcToastAction): number | undefined {
    return this.actionConfirmationRemaining()[this.actionKey(action)];
  }

  private getActionConfirmationSeconds(action: RcToastAction): number {
    return Math.max(0, Math.floor(action.confirmOnHoverSeconds ?? 0));
  }

  private actionKey(action: RcToastAction): string {
    return action.id ?? action.label;
  }

  private variantFromToastType(toastType: string): RcToastVariant {
    if (toastType.includes('toast-success')) return 'success';
    if (toastType.includes('toast-error')) return 'error';
    if (toastType.includes('toast-warning')) return 'warning';
    if (toastType.includes('toast-info')) return 'info';
    return 'default';
  }

  private defaultIconPath(variant: RcToastVariant): string {
    switch (variant) {
      case 'success':
        return 'M5 11.917 9.724 16.5 19 7.5';
      case 'error':
        return 'M6 18 17.94 6M18 18 6.06 6';
      case 'warning':
        return 'M12 13V8m0 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';
      case 'info':
        return 'm12 18-7 3 7-18 7 18-7-3Zm0 0v-5';
      default:
        return 'm12 18-7 3 7-18 7 18-7-3Zm0 0v-5';
    }
  }

  private mergeClasses(requiredClass: string, customClass: string | undefined): string {
    return [requiredClass, customClass].filter(Boolean).join(' ');
  }
}
